import React, { useState } from 'react';
import {
  Table, Button, Space, Input, Select, Card, Typography, Tag, Modal, Form,
  Row, Col, InputNumber, message, Descriptions, Avatar, 
  Statistic, Badge, Tooltip, Divider, Empty
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined,
  SearchOutlined, FilterOutlined, BookOutlined, ClockCircleOutlined,
  DollarOutlined, PercentageOutlined, StarOutlined, UserOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { courseService, type CreateCourseRequest, type GetCoursesParams } from '../services/courseService';
import type { Course, ServiceCategory } from '../types';
import { serviceService } from '../services/serviceService';

const { Title, Text } = Typography;
const { Option } = Select;

const CoursesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useState<GetCoursesParams>({
    page: 1,
    limit: 10,
  });
  const [modal, contextHolder] = Modal.useModal();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // Fetch courses
  const { data: coursesData, isLoading } = useQuery(
    ['courses', searchParams],
    () => courseService.getCourses(searchParams),
    { keepPreviousData: true }
  );

  // Fetch service categories
  const { data: categoriesData } = useQuery(
    ['serviceCategories'],
    () => serviceService.getServiceCategories({ isActive: true })
  );

  // Create/Update course mutation
  const courseMutation = useMutation(
    (data: CreateCourseRequest & { id?: string }) => {
      if (data.id) {
        const { id, ...updateData } = data;
        return courseService.updateCourse(id, updateData);
      }
      return courseService.createCourse(data);
    },
    {
      onSuccess: () => {
        message.success(editingCourse ? 'ແກ້ໄຂຄອສສຳເລັດ' : 'ເພີ່ມຄອສສຳເລັດ');
        setIsModalVisible(false);
        setEditingCourse(null);
        form.resetFields();
        queryClient.invalidateQueries(['courses']);
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'ເກີດຂໍ້ຜິດພາດ');
      },
    }
  );

  // Delete course mutation
  const deleteMutation = useMutation(courseService.deleteCourse, {
    onSuccess: () => {
      message.success('ລຶບຄອສສຳເລັດ');
      queryClient.invalidateQueries(['courses']);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'ເກີດຂໍ້ຜິດພາດ');
    },
  });

  const handleSearch = (value: string) => {
    setSearchParams(prev => ({ ...prev, search: value, page: 1 }));
  };

  const handleCategoryFilter = (category: string) => {
    setSearchParams(prev => ({ ...prev, category: category || undefined, page: 1 }));
  };

  const handleEdit = (course: Course) => {
    setEditingCourse(course);
    form.setFieldsValue({
      ...course,
      category: course.category._id,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (course: Course) => {
    modal.confirm({
      title: 'ຢືນຢັນການລຶບຄອສ',
      content: (
        <div>
          <p>ແນ່ໃຈ ຫຼື ບໍ່ທີ່ຕ້ອງການລຶບຄອສນີ້??</p>
          <div style={{ 
            background: '#f5f5f5', 
            padding: '12px', 
            borderRadius: '6px',
            marginTop: '8px'
          }}>
            <Text strong>{course.name}</Text>
          </div>
        </div>
      ),
      okText: 'ລຶບ',
      okType: 'danger',
      cancelText: 'ຍົກເລິກ',
      onOk: () => deleteMutation.mutate(course._id),
    });
  };

  const handleViewDetails = (course: Course) => {
    setSelectedCourse(course);
    setDetailModalVisible(true);
  };

  const handleSubmit = (values: any) => {
    const data = {
      ...values,
      ...(editingCourse && { id: editingCourse._id }),
    };
    courseMutation.mutate(data);
  };

  const columns = [
    {
      title: 'ຄອສ',
      dataIndex: 'name',
      key: 'name',
      width: 300,
      render: (name: string, record: Course) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Badge dot={record.isActive} color={record.isActive ? 'green' : 'red'}>
            <Avatar
              size={48}
              src={record.image}
              icon={<BookOutlined />}
              style={{ 
                background: record.isActive ? '#52c41a' : '#ff4d4f',
                flexShrink: 0
              }}
            />
          </Badge>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ 
              fontWeight: 600, 
              fontSize: '14px',
              marginBottom: '4px',
              color: record.isActive ? '#262626' : '#8c8c8c'
            }}>
              {name}
            </div>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              fontSize: '12px',
              color: '#8c8c8c'
            }}>
              <ClockCircleOutlined />
              <span>{record.duration} ນາທີ</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'ໝວດໝູ່',
      dataIndex: ['category', 'name'],
      key: 'category',
      width: 150,
      render: (categoryName: string) => (
        <Tag color="processing" style={{ borderRadius: '16px' }}>
          {categoryName}
        </Tag>
      ),
    },
    {
      title: 'ແພັກແກັດລາຄາ',
      dataIndex: 'pricing',
      key: 'pricing',
      width: 200,
      render: (pricing: any[]) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {pricing.slice(0, 2).map((pkg, index) => (
            <div key={index} style={{ 
              background: '#f0f0f0', 
              padding: '4px 8px', 
              borderRadius: '4px',
              fontSize: '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ fontWeight: 500 }}>{pkg.sessions} ຄັ້ງ</span>
              <span style={{ color: '#1890ff' }}>
                {new Intl.NumberFormat('lo-LA').format(pkg.price.LAK)} ₭
              </span>
            </div>
          ))}
          {pricing.length > 2 && (
            <Text type="secondary" style={{ fontSize: '11px', textAlign: 'center' }}>
              +{pricing.length - 2} ແພັກແກັດອື່ນ
            </Text>
          )}
        </div>
      ),
    },
    {
      title: 'ຄ່າຄອມມິດຊັ່ນ',
      dataIndex: 'commissionRate',
      key: 'commissionRate',
      width: 120,
      render: (rate: number) => (
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            background: '#e6f7ff', 
            color: '#1890ff', 
            padding: '4px 8px', 
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: 600
          }}>
            {rate}%
          </div>
        </div>
      ),
    },
    {
      title: 'ສະຖານະ',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'success' : 'error'} style={{ borderRadius: '16px' }}>
          {isActive ? 'ໃຊ້ງານ' : 'ປິດໃຊ້ງານ'}
        </Tag>
      ),
    },
    {
      title: 'ຈັດການ',
      key: 'actions',
      width: 200,
      render: (_: any, record: Course) => (
        <Space size="small">
          <Tooltip title="ລາຍລະອຽດ">
            <Button 
              type="primary" 
              size="small" 
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
              style={{ borderRadius: '6px' }}
            />
          </Tooltip>
          <Tooltip title="ແກ້ໄຂ">
            <Button 
              size="small" 
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              style={{ borderRadius: '6px' }}
            />
          </Tooltip>
          <Tooltip title="ລຶບ">
            <Button 
              danger 
              size="small" 
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
              style={{ borderRadius: '6px' }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const totalCourses = coursesData?.data.pagination.total || 0;
  const activeCourses = coursesData?.data?.courses?.filter((c:any) => c.isActive).length || 0;
  const inactiveCourses = totalCourses - activeCourses;

  return (
    <div style={{minHeight: '100vh'}}>
      <div>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <Title level={2} style={{ margin: 0, color: '#262626' }}>
            <BookOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            ຈັດການຄອສບໍລິການ ແລະ ສິນຄ້າ
          </Title>
          <Text type="secondary" style={{ fontSize: '14px' }}>
            ຈັດການ ແລະ ຄວບຄຸມຄອສບໍລິການທັງໝົດໃນລະບົບ
          </Text>
        </div>

        {/* Statistics Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={8}>
            <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <Statistic
                title="ຄອສທັງໝົດ"
                value={totalCourses}
                prefix={<BookOutlined style={{ color: '#1890ff' }} />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <Statistic
                title="ຄອສທີ່ໃຊ້ງານ"
                value={activeCourses}
                prefix={<StarOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <Statistic
                title="ຄອສທີ່ປິດໃຊ້ງານ"
                value={inactiveCourses}
                prefix={<UserOutlined style={{ color: '#ff4d4f' }} />}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Main Content */}
        <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          {/* Search and Filter Section */}
          <div style={{ 
            background: '#fafafa', 
            padding: '16px', 
            borderRadius: '8px',
            marginBottom: '16px'
          }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Input
                  placeholder="ຄົ້ນຫາຄອສ..."
                  prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                  allowClear
                  onPressEnter={(e) => handleSearch(e.currentTarget.value)}
                  style={{ borderRadius: '8px' }}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Select
                  placeholder="ເບິ່ງຕາມໝວດໝູ່"
                  allowClear
                  style={{ width: '100%' }}
                  onChange={handleCategoryFilter}
                  suffixIcon={<FilterOutlined />}
                >
                  {categoriesData?.data?.categories.map((cat: ServiceCategory) => (
                    <Option key={cat._id} value={cat._id}>{cat.name}</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setIsModalVisible(true)}
                  style={{ 
                    width: '100%', 
                    borderRadius: '8px',
                    height: '40px',
                    fontWeight: 500
                  }}
                >
                  ເພີ່ມຄອສໃໝ່
                </Button>
              </Col>
            </Row>
          </div>

          {/* Table */}
          <Table
            columns={columns}
            dataSource={coursesData?.data?.courses}
            rowKey="_id"
            loading={isLoading}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="ບໍ່ມີຂໍ້ມູນຄອສ"
                />
              )
            }}
            pagination={{
              current: searchParams.page,
              pageSize: searchParams.limit,
              total: coursesData?.data.pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `ສະແດງ ${range[0]}-${range[1]} ຈາກ ${total} ລາຍການ`,
              onChange: (page, pageSize) =>
                setSearchParams(prev => ({ ...prev, page, limit: pageSize })),
            }}
            style={{ 
              borderRadius: '8px',
              overflow: 'hidden'
            }}
          />
        </Card>

        {/* Add/Edit Course Modal */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <BookOutlined style={{ color: '#1890ff' }} />
              {editingCourse ? 'ແກ້ໄຂຄອສ' : 'ເພີ່ມຄອສໃໝ່'}
            </div>
          }
          open={isModalVisible}
          onCancel={() => {
            setIsModalVisible(false);
            setEditingCourse(null);
            form.resetFields();
          }}
          footer={null}
          width={900}
          style={{ top: 20 }}
        >
          <Divider />
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: '8px' }}
          >
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label={<span><BookOutlined /> ຊື່ຄອສ</span>}
                  rules={[{ required: true, message: 'ກະລຸນາປ້ອນຊື່ຄອສ' }]}
                >
                  <Input 
                    placeholder="ຊື່ຄອສ" 
                    style={{ borderRadius: '8px' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="category"
                  label={<span><FilterOutlined /> ໝວດໝູ່</span>}
                  rules={[{ required: true, message: 'ກະລຸນາເລືອກໝວດໝູ່' }]}
                >
                  <Select 
                    placeholder="ເລືອກໝວດໝູ່"
                    style={{ borderRadius: '8px' }}
                  >
                    {categoriesData?.data?.categories.map((cat: ServiceCategory) => (
                      <Option key={cat._id} value={cat._id}>{cat.name}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="duration"
                  label={<span><ClockCircleOutlined /> ໄລຍະເວລາ (ນາທີ)</span>}
                  rules={[{ required: true, message: 'ກະລຸນາປ້ອນໄລຍະເວລາ' }]}
                >
                  <InputNumber
                    min={1}
                    style={{ width: '100%', borderRadius: '8px' }}
                    placeholder="ໄລຍະເວລາ"
                  />
                </Form.Item>
              </Col>

            </Row>

            <Form.Item name="description" label="ຄຳອະທິບາຍ">
              <Input.TextArea 
                rows={3} 
                placeholder="ຄຳອະທິບາຍຄອສ" 
                style={{ borderRadius: '8px' }}
              />
            </Form.Item>

            <Form.Item name="image" label="ຮູບ (URL)">
              <Input 
                placeholder="URL ຮູບ" 
                style={{ borderRadius: '8px' }}
              />
            </Form.Item>

            <Form.Item label={<span><DollarOutlined /> ແພັກແກັດລາຄາ</span>}>
              <Form.List name="pricing">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Card 
                        key={key} 
                        size="small" 
                        style={{ 
                          marginBottom: 12,
                          borderRadius: '8px',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
                        }}
                      >
                        <Row gutter={12}>
                          <Col span={6}>
                            <Form.Item
                              {...restField}
                              name={[name, 'sessions']}
                              label="ຈຳນວນຄັ້ງ"
                              rules={[{ required: true, message: 'ກະລຸນາປ້ອນຈຳນວນຄັ້ງ' }]}
                            >
                              <InputNumber 
                                min={1} 
                                style={{ width: '100%', borderRadius: '6px' }} 
                              />
                            </Form.Item>
                          </Col>
                          <Col span={5}>
                            <Form.Item
                              {...restField}
                              name={[name, 'price', 'LAK']}
                              label="ລາຄາ LAK"
                              rules={[{ required: true, message: 'ກະລຸນາປ້ອນລາຄາ' }]}
                            >
                              <InputNumber 
                                min={0} 
                                style={{ width: '100%', borderRadius: '6px' }} 
                              />
                            </Form.Item>
                          </Col>
                          <Col span={5}>
                            <Form.Item
                              {...restField}
                              name={[name, 'price', 'THB']}
                              label="ລາຄາ THB"
                            >
                              <InputNumber 
                                min={0} 
                                style={{ width: '100%', borderRadius: '6px' }} 
                              />
                            </Form.Item>
                          </Col>
                          <Col span={5}>
                            <Form.Item
                              {...restField}
                              name={[name, 'price', 'USD']}
                              label="ລາຄາ USD"
                            >
                              <InputNumber 
                                min={0} 
                                style={{ width: '100%', borderRadius: '6px' }} 
                              />
                            </Form.Item>
                          </Col>
                          <Col span={3}>
                            <Button 
                              type="text" 
                              danger 
                              onClick={() => remove(name)}
                              style={{ 
                                marginTop: 30,
                                borderRadius: '6px'
                              }}
                            >
                              ລຶບ
                            </Button>
                          </Col>
                        </Row>
                      </Card>
                    ))}
                    <Button 
                      type="dashed" 
                      onClick={() => add()} 
                      block
                      style={{ 
                        borderRadius: '8px',
                        height: '40px',
                        borderStyle: 'dashed'
                      }}
                    >
                      <PlusOutlined /> ເພີ່ມແພັກແກັດ
                    </Button>
                  </>
                )}
              </Form.List>
            </Form.Item>

            <Divider />

            <Form.Item style={{ marginBottom: 0 }}>
              <Space>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={courseMutation.isLoading}
                  style={{ 
                    borderRadius: '8px',
                    paddingInline: '32px',
                    height: '40px'
                  }}
                >
                  {editingCourse ? 'ແກ້ໄຂ' : 'ເພີ່ມຄອສ'}
                </Button>
                <Button 
                  onClick={() => {
                    setIsModalVisible(false);
                    setEditingCourse(null);
                    form.resetFields();
                  }}
                  style={{ 
                    borderRadius: '8px',
                    paddingInline: '32px',
                    height: '40px'
                  }}
                >
                  ຍົກເລິກ
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Course Detail Modal */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <BookOutlined style={{ color: '#1890ff' }} />
              ລາຍລະອຽດຄອສ
            </div>
          }
          open={detailModalVisible}
          onCancel={() => {
            setDetailModalVisible(false);
            setSelectedCourse(null);
          }}
          footer={null}
          width={700}
        >
          {selectedCourse && (
            <div style={{ paddingTop: '16px' }}>
              <div style={{ 
                textAlign: 'center', 
                marginBottom: '24px',
                padding: '16px',
                background: '#f8f9fa',
                borderRadius: '8px'
              }}>
                <Avatar
                  size={80}
                  src={selectedCourse.image}
                  icon={<BookOutlined />}
                  style={{ marginBottom: '8px' }}
                />
                <Title level={3} style={{ margin: '8px 0' }}>
                  {selectedCourse.name}
                </Title>
                <Tag color={selectedCourse.isActive ? 'success' : 'error'}>
                  {selectedCourse.isActive ? 'ໃຊ້ງານ' : 'ປິດໃຊ້ງານ'}
                </Tag>
              </div>

              <Descriptions bordered column={2} style={{ marginBottom: '16px' }}>
                <Descriptions.Item label="ໝວດໝູ່">
                  <Tag color="processing">{selectedCourse.category.name}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="ໄລຍະເວລາ">
                  <Space>
                    <ClockCircleOutlined />
                    {selectedCourse.duration} ນາທີ
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="ຄ່າຄອມມິດຊົ່ນ">
                  <Space>
                    <PercentageOutlined />
                    {selectedCourse.commissionRate}%
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="ຈຳນວນແພັກແກັດ">
                  {selectedCourse.pricing.length} ແພັກແກັດ
                </Descriptions.Item>
                {selectedCourse.description && (
                  <Descriptions.Item label="ຄຳອະທິບາຍ" span={2}>
                    {selectedCourse.description}
                  </Descriptions.Item>
                )}
              </Descriptions>

              <Title level={4} style={{ marginTop: '24px', marginBottom: '16px' }}>
                <DollarOutlined /> ແພັກແກັດລາຄາ
              </Title>
              <Row gutter={[16, 16]}>
                {selectedCourse.pricing.map((pkg, index) => (
                  <Col span={8} key={index}>
                    <Card 
                      size="small"
                      style={{ 
                        textAlign: 'center',
                        borderRadius: '8px',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
                      }}
                    >
                      <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
                        {pkg.sessions} ຄັ້ງ
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        <div style={{ color: '#1890ff', fontWeight: 500 }}>
                          {new Intl.NumberFormat('lo-LA').format(pkg.price.LAK)} ₭
                        </div>
                        {pkg.price.THB > 0 && (
                          <div>{new Intl.NumberFormat('th-TH').format(pkg.price.THB)} ฿</div>
                        )}
                        {pkg.price.USD > 0 && (
                          <div>${new Intl.NumberFormat('en-US').format(pkg.price.USD)}</div>
                        )}
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </Modal>
      </div>

         {contextHolder}
    </div>
  );
};

export default CoursesPage;